import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import {
  getLoggedinUser,
  TENANT_SESSION_UPDATED_EVENT,
} from "../../helpers/api_helper";
import { getInitials } from "../../helpers/userDisplay";
import { isOwner } from "../../utils/tenantRoles";

/** Avatar circular fixo — evita “pular” layout com iniciais de 1 vs 2 letras. */
const avatarStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  minWidth: 32,
  fontSize: 12,
  lineHeight: "32px",
};

/**
 * Menu do usuário no Header tenant (LAC-011 / DOC 09 § 4.1).
 * Só ações F1: settings reais + logout. Sem demos Velzon (Messages, Balance, etc.).
 * Nome/e-mail vêm do JWT em sessionStorage — não do avatar fictício do template.
 */
const ProfileDropdown = () => {
  const { t } = useTranslation();
  const [isProfileDropdown, setIsProfileDropdown] = useState(false);
  // Força re-leitura após PATCH /me (sessionStorage sozinho não re-renderiza).
  const [, setSessionTick] = useState(0);
  const [avatarBroken, setAvatarBroken] = useState(false);

  useEffect(() => {
    const onUpdate = () => setSessionTick((n) => n + 1);
    window.addEventListener(TENANT_SESSION_UPDATED_EVENT, onUpdate);
    return () =>
      window.removeEventListener(TENANT_SESSION_UPDATED_EVENT, onUpdate);
  }, []);

  const session = getLoggedinUser();
  const displayName =
    session?.user?.name ||
    session?.user?.email ||
    session?.tenant?.name ||
    t("menu.account");
  const email = session?.user?.email || "";
  const avatarUrl =
    typeof session?.user?.avatar_url === "string" && session.user.avatar_url
      ? session.user.avatar_url
      : null;

  useEffect(() => {
    setAvatarBroken(false);
  }, [avatarUrl]);

  const initials = getInitials(
    session?.user?.name || email || displayName,
    "U"
  );
  const showMfaNudge =
    isOwner() && session?.user?.mfa_enabled === false;

  return (
    <Dropdown
      isOpen={isProfileDropdown}
      toggle={() => setIsProfileDropdown(!isProfileDropdown)}
      className="ms-sm-3 header-item topbar-user"
    >
      <DropdownToggle tag="button" type="button" className="btn">
        <span className="d-flex align-items-center">
          {avatarUrl && !avatarBroken ? (
            <img
              src={avatarUrl}
              alt=""
              className="rounded-circle header-profile-user"
              style={avatarStyle}
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <span
              className="rounded-circle header-profile-user bg-primary text-dark d-inline-flex align-items-center justify-content-center fw-semibold"
              style={avatarStyle}
              aria-hidden="true"
            >
              {initials}
            </span>
          )}
          <span className="text-start ms-xl-2">
            <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">
              {displayName}
            </span>
            {email ? (
              <span className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">
                {email}
              </span>
            ) : null}
          </span>
        </span>
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-end">
        <h6 className="dropdown-header">
          {t("menu.welcome", { name: displayName })}
        </h6>
        <DropdownItem className="p-0" tag={Link} to="/settings/account">
          <span className="dropdown-item">
            <i className="mdi mdi-account-circle-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">{t("menu.myAccount")}</span>
          </span>
        </DropdownItem>
        {showMfaNudge ? (
          <DropdownItem className="p-0" tag={Link} to="/settings/account">
            <span className="dropdown-item">
              <i className="mdi mdi-shield-key-outline text-warning fs-16 align-middle me-1"></i>
              <span className="align-middle">{t("menu.enableMfa")}</span>
            </span>
          </DropdownItem>
        ) : null}
        <DropdownItem className="p-0" tag={Link} to="/settings/sending">
          <span className="dropdown-item">
            <i className="mdi mdi-email-check-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">{t("menu.sendingSetup")}</span>
          </span>
        </DropdownItem>
        <DropdownItem className="p-0" tag={Link} to="/settings/subscription">
          <span className="dropdown-item">
            <i className="mdi mdi-credit-card-outline text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">{t("menu.subscription")}</span>
          </span>
        </DropdownItem>
        <div className="dropdown-divider"></div>
        <DropdownItem className="p-0" tag={Link} to="/logout">
          <span className="dropdown-item">
            <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">{t("menu.logout")}</span>
          </span>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export default ProfileDropdown;
